import { Component, OnInit } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';
import {
  FieldKeyItem,
  FieldKeyItemSource,
  HomePageColumnConfiguration,
  InspectionAppConfigurationService
} from 'src/app/services/inspection-app-configuration.service';
import { WorkflowContextService } from 'src/app/services';

interface WorkingMapping {
  fieldKey: string;
  source: FieldKeyItemSource;
}

interface WorkingColumn {
  label: string;
  mappings: WorkingMapping[];
}

interface ColumnSection {
  columns: WorkingColumn[];
  newColumnLabel: string;
  pendingDeleteIndex: number | null;
}

@Component({
  selector: 'app-settings-inspection-app-configuration',
  standalone: false,
  templateUrl: './settings-inspection-app-configuration.component.html',
  styleUrls: ['./settings-inspection-app-configuration.component.scss']
})
export class SettingsInspectionAppConfigurationComponent implements OnInit {
  clientId = '';
  departmentId = '';
  availableFields: FieldKeyItem[] = [];
  isDirty = false;
  isLoading = false;

  homePage: ColumnSection = this.createEmptySection();
  appDetails: ColumnSection = this.createEmptySection();
  FieldKeyItemSource = FieldKeyItemSource;

  removeItemModal: CommonConfirmationModalData = {
    open: false,
    yesNoBtn: true,
    replaceYesWith: 'REMOVE',
    replaceNoWith: 'CANCEL',
    modalDescp: 'Remove item?',
    modalSubDescp: 'Are you sure you want to remove this item?'
  };

  discardChangesModal: CommonConfirmationModalData = {
    open: false,
    yesNoBtn: true,
    replaceYesWith: 'DISCARD CHANGES',
    replaceNoWith: 'KEEP EDITING',
    modalDescp: 'Discard unsaved changes?',
    modalSubDescp: 'Your unsaved changes will be lost when you close this configuration.'
  };

  deleteConfigurationModal: CommonConfirmationModalData = {
    open: false,
    yesNoBtn: true,
    replaceYesWith: 'DELETE',
    replaceNoWith: 'CANCEL',
    modalDescp: 'Delete inspection app configuration?',
    modalSubDescp: 'This clears all configured Home Page columns and Application Details.'
  };

  private pendingDeleteSection: ColumnSection | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private inspectionAppConfigurationService: InspectionAppConfigurationService,
    private toastr: ToastrService,
    private context: WorkflowContextService
  ) {}

  ngOnInit(): void {
    this.clientId = this.context.client?.id || '';
    this.departmentId =
      this.route.snapshot.paramMap.get('id') ||
      this.route.snapshot.paramMap.get('departmentId') ||
      this.context.staffDepartmentId ||
      '';

    if (!this.clientId || !this.departmentId) {
      this.toastr.error('Unable to determine the jurisdiction or department');
      return;
    }

    this.loadConfiguration();
  }

  private createEmptySection(): ColumnSection {
    return { columns: [], newColumnLabel: '', pendingDeleteIndex: null };
  }

  private toWorkingColumns(
    config: { [key: number]: HomePageColumnConfiguration } | undefined | null
  ): WorkingColumn[] {
    if (!config || Object.keys(config).length === 0) {
      return [];
    }

    return Object.keys(config)
      .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
      .map(key => {
        const column = config[parseInt(key, 10)];
        return {
          label: column.label ?? '',
          mappings: (column.mappings ?? [])
            .sort((a, b) => a.priority - b.priority)
            .map(mapping => ({
              fieldKey: mapping.fieldKey,
              source: mapping.source
            }))
        };
      });
  }

  private toConfigurationDict(
    columns: WorkingColumn[]
  ): { [key: number]: HomePageColumnConfiguration } {
    const configuration: { [key: number]: HomePageColumnConfiguration } = {};
    columns.forEach((column, columnIndex) => {
      configuration[columnIndex + 1] = {
        label: column.label,
        mappings: column.mappings.map((mapping, mappingIndex) => ({
          priority: mappingIndex + 1,
          fieldKey: mapping.fieldKey,
          source: mapping.source
        }))
      };
    });
    return configuration;
  }

  loadConfiguration(): void {
    this.isLoading = true;
    this.inspectionAppConfigurationService
      .getAvailableFields(this.clientId, this.departmentId)
      .subscribe({
        next: response => {
          this.availableFields = response.availableFields ?? [];
          this.loadSavedConfiguration();
        },
        error: () => {
          this.toastr.error('Failed to load available fields');
          this.isLoading = false;
        }
      });
  }

  private loadSavedConfiguration(): void {
    this.inspectionAppConfigurationService
      .getConfiguration(this.clientId, this.departmentId)
      .subscribe({
        next: response => {
          this.homePage = {
            columns: this.toWorkingColumns(response.homePageColumns),
            newColumnLabel: '',
            pendingDeleteIndex: null
          };
          this.appDetails = {
            columns: this.toWorkingColumns(response.applicationDetails),
            newColumnLabel: '',
            pendingDeleteIndex: null
          };
          this.isDirty = false;
          this.isLoading = false;
        },
        error: () => {
          this.toastr.error('Failed to load configuration');
          this.isLoading = false;
        }
      });
  }

  addColumn(section: ColumnSection): void {
    const label = section.newColumnLabel.trim();
    if (!label) {
      this.toastr.warning('Please enter a label');
      return;
    }

    section.columns.push({ label, mappings: [] });
    section.newColumnLabel = '';
    this.isDirty = true;
  }

  requestDeleteColumn(section: ColumnSection, index: number): void {
    section.pendingDeleteIndex = index;
    this.pendingDeleteSection = section;
    this.removeItemModal = { ...this.removeItemModal, open: true };
  }

  confirmDeleteColumn(): void {
    const section = this.pendingDeleteSection;
    if (section && section.pendingDeleteIndex !== null) {
      section.columns.splice(section.pendingDeleteIndex, 1);
      section.pendingDeleteIndex = null;
      this.isDirty = true;
    }
    this.pendingDeleteSection = null;
    this.removeItemModal = { ...this.removeItemModal, open: false };
  }

  cancelDeleteColumn(): void {
    if (this.pendingDeleteSection) {
      this.pendingDeleteSection.pendingDeleteIndex = null;
    }
    this.pendingDeleteSection = null;
    this.removeItemModal = { ...this.removeItemModal, open: false };
  }

  addFieldMapping(
    section: ColumnSection,
    columnIndex: number,
    field: FieldKeyItem
  ): void {
    if (!field) return;
    section.columns[columnIndex].mappings.push({
      fieldKey: field.fieldKey,
      source: field.source
    });
    this.isDirty = true;
  }

  removeMapping(
    section: ColumnSection,
    columnIndex: number,
    mappingIndex: number
  ): void {
    section.columns[columnIndex].mappings.splice(mappingIndex, 1);
    this.isDirty = true;
  }

  dropColumn(event: CdkDragDrop<WorkingColumn[]>, section: ColumnSection): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(section.columns, event.previousIndex, event.currentIndex);
      this.isDirty = true;
    }
  }

  dropMapping(
    event: CdkDragDrop<WorkingMapping[]>,
    section: ColumnSection,
    columnIndex: number
  ): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        section.columns[columnIndex].mappings,
        event.previousIndex,
        event.currentIndex
      );
      this.isDirty = true;
    }
  }

  save(): void {
    this.isLoading = true;
    this.inspectionAppConfigurationService
      .saveConfiguration(
        this.clientId,
        this.departmentId,
        this.toConfigurationDict(this.homePage.columns),
        this.toConfigurationDict(this.appDetails.columns)
      )
      .subscribe({
        next: () => {
          this.toastr.success('Configuration saved successfully');
          this.isDirty = false;
          this.isLoading = false;
        },
        error: () => {
          this.toastr.error('Failed to save configuration');
          this.isLoading = false;
        }
      });
  }

  close(): void {
    if (this.isDirty) {
      this.discardChangesModal = {
        ...this.discardChangesModal,
        open: true
      };
      return;
    }
    this.navigateToDepartment();
  }

  confirmClose(): void {
    this.discardChangesModal = {
      ...this.discardChangesModal,
      open: false
    };
    this.navigateToDepartment();
  }

  closeDiscardChangesModal(): void {
    this.discardChangesModal = {
      ...this.discardChangesModal,
      open: false
    };
  }

  requestDeleteConfiguration(): void {
    this.deleteConfigurationModal = {
      ...this.deleteConfigurationModal,
      open: true
    };
  }

  confirmDeleteConfiguration(): void {
    this.deleteConfigurationModal = {
      ...this.deleteConfigurationModal,
      open: false
    };
    this.isLoading = true;
    this.inspectionAppConfigurationService
      .deleteConfiguration(this.clientId, this.departmentId)
      .subscribe({
        next: () => {
          this.toastr.success('Configuration deleted successfully');
          this.homePage = this.createEmptySection();
          this.appDetails = this.createEmptySection();
          this.isDirty = false;
          this.isLoading = false;
        },
        error: () => {
          this.toastr.error('Failed to delete configuration');
          this.isLoading = false;
        }
      });
  }

  closeDeleteConfigurationModal(): void {
    this.deleteConfigurationModal = {
      ...this.deleteConfigurationModal,
      open: false
    };
  }

  private navigateToDepartment(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  hasAnyConfiguration(): boolean {
    return this.homePage.columns.length > 0 || this.appDetails.columns.length > 0;
  }

  hasAnyAvailableFields(section: ColumnSection, columnIndex: number): boolean {
    const usedKeys = new Set(
      section.columns[columnIndex].mappings.map(mapping => mapping.fieldKey)
    );
    return this.availableFields.some(field => !usedKeys.has(field.fieldKey));
  }

  getFieldsForMapping(
    section: ColumnSection,
    columnIndex: number,
    source: FieldKeyItemSource
  ): FieldKeyItem[] {
    const usedKeys = new Set(
      section.columns[columnIndex].mappings.map(mapping => mapping.fieldKey)
    );
    return this.availableFields.filter(
      field => field.source === source && !usedKeys.has(field.fieldKey)
    );
  }

  getConfiguredFieldLabel(fieldKey: string, source: FieldKeyItemSource): string {
    const label =
      this.availableFields.find(field => field.fieldKey === fieldKey)?.label ??
      fieldKey;
    if (
      source !== FieldKeyItemSource.InspectionCustomFields ||
      label === fieldKey
    ) {
      return label;
    }
    return `${label} (${fieldKey})`;
  }

  getSourceLabel(source: FieldKeyItemSource): string {
    switch (source) {
      case FieldKeyItemSource.Application:
        return 'System Application Field';
      case FieldKeyItemSource.Inspection:
        return 'System Inspection Field';
      case FieldKeyItemSource.InspectionCustomFields:
        return 'Custom Inspection Field';
      default:
        return 'Unknown';
    }
  }

  getInspectionCustomFieldOptionLabel(field: FieldKeyItem): string {
    const label = field.label?.trim() || field.fieldKey;
    return label === field.fieldKey ? field.fieldKey : `${label} (${field.fieldKey})`;
  }
}
